import React, { useState, useContext, useEffect } from "react";
import {
  Menu,
  ChevronLeft,
  Moon,
  Sun,
  MessageSquare,
  UserCircle,
  PanelLeft,
  PanelTop,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { cn } from "../lib/utils";

import MainListItems from "./MainListItems";
import TopNav from "./TopNav";
import SaudacaoDoUsuario from "../components/SaudacaoDoUsuario";
import BottomNav from "./BottomNav";
import MenuDeIcones from "./MenuDeIcones";
import { lerPreferencia, gravarPreferencia } from "../services/preferencias";
import InstalarApp from "../components/InstalarApp";
import AtualizacaoDisponivel from "../components/AtualizacaoDisponivel";
import GlobalSearch from "../components/GlobalSearch";
import NotificationCenter from "../components/NotificationCenter";
import AnnouncementsAutoPopup from "../components/AnnouncementsAutoPopup";
import UserModal from "../components/UserModal";
import DialPad from "../components/DialPad";
import InternalChatWidget from "../components/InternalChatWidget";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { useWhiteLabel } from "../context/WhiteLabel";
import { getPublicFileUrl } from "../config";

// 264 e não 240: com o recuo dos subitens, rótulos como "Cargos e permissões"
// e "Mensagens agendadas" chegavam ao limite e eram cortados com reticências.
// Nome de menu truncado obriga a adivinhar para onde o item leva.
const SIDEBAR_WIDTH = 264;
const SIDEBAR_WIDTH_COLLAPSED = 72;

// Onde o menu mora, no desktop: "lateral" (padrão, como sempre foi) ou "topo".
// Vai em cookie porque é assim que este projeto guarda preferência de tela —
// o localStorage saiu de propósito para layout, mas o VOLUME de notificações
// PRECISA ir em localStorage (cache resiliente) ALÉM de ir no banco
// (fonte da verdade multi-dispositivo). É persistência em 2 camadas.
const CHAVE_MODO_MENU = "layout:modoMenu";
const LS_VOLUME_PREFIX = "PERABIT_NOTIF_VOLUME_v1_";
const chaveLsVolume = (userId) => `${LS_VOLUME_PREFIX}${Number(userId) || 0}`;
const lerLsVolume = (userId) => {
  try {
    const raw = localStorage.getItem(chaveLsVolume(userId));
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(100, n));
  } catch (_) {
    return null;
  }
};
const gravarLsVolume = (userId, valor) => {
  try {
    if (!userId) return;
    const v = Math.max(0, Math.min(100, Number(valor) || 0));
    localStorage.setItem(chaveLsVolume(userId), String(v));
  } catch (_) {}
};

function SidebarContent({ collapsed, drawerClose }) {
  // A marca da empresa, não a nossa: quem configurou nome e logo espera
  // vê-los aqui, e não "WhaTicket" fixo.
  const { appName, logoUrl } = useWhiteLabel();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 px-4">
        {logoUrl ? (
          <img
            src={getPublicFileUrl(logoUrl)}
            alt={appName}
            className="h-8 w-8 shrink-0 rounded-lg object-contain"
          />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <MessageSquare className="h-4 w-4" />
          </div>
        )}
        {!collapsed && (
          <span className="truncate text-base font-semibold tracking-tight">
            {appName}
          </span>
        )}
      </div>
      <div className="scrollbar-discreta flex-1 overflow-y-auto py-2">
        <MainListItems drawerClose={drawerClose} collapsed={collapsed} />
      </div>
    </div>
  );
}

const LoggedInLayout = ({ children }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const { handleLogout, loading, handleUpdateMuteGroupSounds, handleUpdateNotificationVolume } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const { appName, logoUrl } = useWhiteLabel();

  // Estado local instantâneo para o slider de volume (evita lag visual
  // enquanto salva no backend via PATCH).
  //
  // Inicialização: 3 fontes em ORDEM DE PRIORIDADE:
  //   1) localStorage do MESMO userId (SE user.id JÁ existe, p.ex. após
  //      HMR ou 2º render). Antes do /me voltar userId = undefined →
  //      PULAR para não ler chave errada PERABIT_NOTIF_VOLUME_v1_0.
  //   2) user.notificationVolume (valor do banco retornado pelo /me)
  //   3) fallback 50% (nunca fica "NaN", é o padrão da coluna do banco)
  const [volumeLocal, setVolumeLocal] = useState(() => {
    if (user?.id) {
      const doLs = lerLsVolume(user.id);
      if (Number.isFinite(doLs)) return doLs;
    }
    const doBanco = Math.max(0, Math.min(100, Number(user?.notificationVolume) || 0));
    if (Number.isFinite(doBanco) && user?.id) return doBanco;
    return 50;
  });

  // Quando a preferência muda por fora (outro navegador, socket, modal
  // perfil PUT, etc.), espelha o valor novo no slider local.
  // Também escreve no localStorage para manter cache coerente.
  useEffect(() => {
    const novoDb = Math.max(0, Math.min(100, Number(user?.notificationVolume) || 0));
    if (!Number.isFinite(novoDb)) return;
    setVolumeLocal((antigo) => {
      // Evita sobrescrever se o usuário estiver arrastando AGORA
      // (diferença ≤ 0 significa nenhuma alteração nova).
      // Usamos === com o banco: se DB mudou, UI segue.
      return novoDb;
    });
    gravarLsVolume(user?.id, novoDb);
  }, [user?.id, user?.notificationVolume]);

  const VolumeIcone = () => {
    const v = Math.max(0, Math.min(100, Number(volumeLocal) || 0));
    if (v <= 0) return <VolumeX className="h-4 w-4 text-muted-foreground" />;
    if (v <= 40) return <Volume1 className="h-4 w-4 text-muted-foreground" />;
    return <Volume2 className="h-4 w-4 text-muted-foreground" />;
  };

  const aplicarVolume = async (novoValor) => {
    const valor = Math.max(0, Math.min(100, Number(novoValor) || 0));
    setVolumeLocal(valor);
    // Salva IMEDIATAMENTE no localStorage (antes do PATCH voltar).
    // Se a requisição cair (offline, timeout, erro 5xx) a escolha do
    // usuário NÃO se perde: no reload o localStorage prevalece e
    // sincroniza depois quando rede voltar.
    gravarLsVolume(user?.id, valor);
    try {
      await handleUpdateNotificationVolume(valor);
    } catch (_e) {
      /* toast já é emitido dentro de handleUpdateNotificationVolume */
    }
  };

  // Só vale do md: para cima. No celular a navegação é a barra de baixo mais a
  // bandeja de ícones, e nada disso muda com esta preferência.
  const [modoMenu, setModoMenu] = useState(() =>
    lerPreferencia(CHAVE_MODO_MENU, "lateral") === "topo" ? "topo" : "lateral"
  );
  const modoTopo = modoMenu === "topo";

  const alternarModoMenu = () => {
    setModoMenu((anterior) => {
      const novo = anterior === "topo" ? "lateral" : "topo";
      gravarPreferencia(CHAVE_MODO_MENU, novo);
      return novo;
    });
  };

  useEffect(() => {
    const checkSize = () => {
      const mobile = document.body.offsetWidth < 600;
      setIsMobile(mobile);
      if (!mobile) setDrawerOpen(true);
    };
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  const drawerClose = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
  };

  const handleClickLogout = () => {
    handleLogout();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  const sidebarWidth = drawerOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED;

  return (
    // h-dvh, não h-screen: no celular o 100vh do `h-screen` conta a área que
    // a barra de endereço do navegador ocupa, então o rodapé da tela — a
    // caixa de digitar mensagem — ficava abaixo da parte visível e aparecia
    // cortado. O dvh acompanha a barra aparecendo e sumindo.
    <div className="flex h-dvh w-full overflow-hidden bg-background">
      {/* Barra lateral do desktop. No modo "topo" ela não é escondida por CSS:
          sai da árvore mesmo, senão o MainListItems continuaria montado e o
          conteúdo não ganharia a largura toda. */}
      {!modoTopo && (
        <aside
          className={cn(
            "hidden shrink-0 border-r bg-card transition-[width] duration-200 ease-in-out md:block"
          )}
          style={{ width: sidebarWidth }}
        >
          <SidebarContent collapsed={!drawerOpen} drawerClose={drawerClose} />
        </aside>
      )}

      {/* No celular não há gaveta lateral: o "Mais" da barra de baixo abre
          uma bandeja com todos os ícones. Gaveta que entra pelo lado é padrão
          de desktop — cobre o conteúdo por uma borda só e obriga a percorrer
          38 linhas verticais para achar uma tela. */}
      <MenuDeIcones
        aberto={mobileOpen}
        aoFechar={() => setMobileOpen(false)}
        aoAbrirPerfil={handleOpenUserModal}
        aoSair={handleClickLogout}
      />

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        {/* pt com a safe area: aberto como app na tela de início, o topo cai
            atrás do relógio e da bateria do iPhone. */}
        {/* Barra de cima: só do tablet para cima.
            No celular ela custava 56px de altura em toda tela para carregar
            um menu que agora é a barra de baixo e ações de conta que passaram
            para a bandeja do "Mais". Busca e avisos foram para a barra de
            baixo, que é onde o polegar alcança. */}
        <header className="hidden min-h-14 shrink-0 items-center gap-3 border-b bg-background px-4 pt-[env(safe-area-inset-top)] md:flex">
          {/* O menu completo, no celular.
              A barra de baixo cobre os quatro destinos do dia a dia, mas ela
              se recolhe dentro de uma conversa — e lá o menu ficava sem
              nenhuma porta de entrada. Este botão existe em toda tela, que é
              o que se espera de um sanduíche. */}
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 md:hidden"
            aria-label="Abrir menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          {/* Recolher a lateral só faz sentido quando ela existe. */}
          {!modoTopo && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:inline-flex"
              onClick={() => setDrawerOpen((prev) => !prev)}
            >
              <ChevronLeft
                className={cn(
                  "h-5 w-5 transition-transform",
                  !drawerOpen && "rotate-180"
                )}
              />
            </Button>
          )}

          {/* A marca aparece no topo do celular: sem a barra lateral fixa, a
              tela não dizia em que sistema a pessoa está. No modo topo ela
              precisa aparecer no desktop também, pelo mesmo motivo: quem
              carregava logo e nome era o cabeçalho da lateral, que sumiu. */}
          {modoTopo && (
            <div className="hidden shrink-0 items-center gap-2 md:flex">
              {logoUrl ? (
                <img
                  src={getPublicFileUrl(logoUrl)}
                  alt={appName}
                  className="h-7 w-7 shrink-0 rounded-lg object-contain"
                />
              ) : (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <MessageSquare className="h-4 w-4" />
                </div>
              )}
              <span className="hidden truncate text-base font-semibold tracking-tight lg:inline">
                {appName}
              </span>
            </div>
          )}

          <span className="truncate text-base font-semibold tracking-tight md:hidden">
            {appName}
          </span>

          {/* No modo topo a navegação ocupa o espaço livre da barra; no modo
              lateral, esse espaço passou a levar a saudação — quem está
              usando, por qual empresa e até quando o acesso vale. */}
          {modoTopo ? <TopNav /> : <SaudacaoDoUsuario />}

          {/* O par sol/lua com o interruptor ocupava três espaços da barra —
              no celular sobrava pouco para busca e avisos, que são o que se
              usa. Ali ele vira um item do menu do usuário. */}
          <div className="hidden shrink-0 items-center gap-1 md:flex">
            <Sun className="h-4 w-4 text-muted-foreground" />
            <Switch checked={darkMode} onCheckedChange={toggleTheme} />
            <Moon className="h-4 w-4 text-muted-foreground" />
          </div>

          {user.id && <GlobalSearch />}
          {user.id && <NotificationCenter />}

          {user.id && <InternalChatWidget />}

          {/* Discador global da Wavoip: teclado numérico para digitar e ligar
              para qualquer número sem precisar abrir um ticket. Fica fixo no
              cabeçalho para ficar acessível de qualquer tela do painel. */}
          {user.id && <DialPad />}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="overflow-hidden">
                {user?.profilePicUrl ? (
                  <img
                    src={getPublicFileUrl(user.profilePicUrl)}
                    alt={user?.name || "avatar"}
                    className="h-7 w-7 shrink-0 rounded-full object-cover border border-border"
                    onError={(e) => {
                      // Se a imagem quebrar (arquivo apagado, URL inválida),
                      // volta para o ícone padrão sem ficar com ícone "quebrado".
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <UserCircle className="h-5 w-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="md:hidden"
                onSelect={(e) => {
                  // Sem isto o menu fecha antes de o tema trocar, e o toque
                  // parece não ter feito nada.
                  e.preventDefault();
                  toggleTheme();
                }}
              >
                {darkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
                {darkMode ? "Tema claro" : "Tema escuro"}
              </DropdownMenuItem>
              {/* Onde o menu fica é preferência de aparência, da mesma família
                  do tema — e este já é o lugar do tema. Uma opção usada uma vez
                  na vida não merece espaço fixo na barra, e aqui ela fica no
                  mesmo canto em todas as telas. Só no desktop: no celular a
                  navegação é outra (barra de baixo + bandeja) e o item seria uma
                  chave que não liga nada. */}
              <DropdownMenuItem
                className="hidden md:flex"
                onSelect={(e) => {
                  // Igual ao tema: sem isto o menu fecha antes de a tela se
                  // remontar e o clique parece não ter feito nada.
                  e.preventDefault();
                  alternarModoMenu();
                }}
              >
                {modoTopo ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelTop className="h-4 w-4" />
                )}
                {modoTopo ? "Menu lateral" : "Menu no topo"}
              </DropdownMenuItem>
              {/* Toggle rápido de som de grupos no próprio menu do usuário —
                  não precisa abrir modal nem pedir ajuda para o admin. */}
              <DropdownMenuItem
                onSelect={(e) => {
                  // Evita que o menu feche antes do Switch registrar o toggle.
                  e.preventDefault();
                }}
                className="gap-2 cursor-default"
              >
                {user?.muteGroupSounds ? (
                  <VolumeX className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                )}
                <div className="flex flex-1 items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium leading-none">
                      Silenciar grupos
                    </p>
                    <p className="text-[11px] leading-none text-muted-foreground">
                      Só o som; notificações e badge continuam.
                    </p>
                  </div>
                  <Switch
                    checked={!!user?.muteGroupSounds}
                    onCheckedChange={(v) => {
                      handleUpdateMuteGroupSounds(v);
                    }}
                  />
                </div>
              </DropdownMenuItem>

              {/* Slider rápido de intensidade do som de notificação.
                  Padrão 50% — não toca muito forte nem muito baixo.
                  Valor salvo no banco por usuário (igual muteGroupSounds),
                  então logar em outro PC já traz a mesma preferência. */}
              <DropdownMenuItem
                onSelect={(e) => {
                  // Evita que o menu feche ao arrastar o slider.
                  e.preventDefault();
                }}
                className="gap-2 cursor-default flex-col items-stretch py-3"
              >
                <div className="flex items-center gap-2">
                  <VolumeIcone />
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium leading-none">
                        Volume notificações
                      </p>
                      <p className="text-[11px] leading-none text-muted-foreground">
                        Chamada, chat interno e sons de mensagem nova.
                      </p>
                    </div>
                    <span className="inline-flex h-7 min-w-[3rem] items-center justify-center rounded-md border border-border bg-muted px-2 text-[11px] font-medium text-muted-foreground tabular-nums">
                      {Math.max(0, Math.min(100, Number(volumeLocal) || 0))}%
                    </span>
                  </div>
                </div>
                <div className="mt-2 px-6">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.max(0, Math.min(100, Number(volumeLocal) || 0))}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                      setVolumeLocal(v);
                    }}
                    onCommit={(e) => {
                      const v = Math.max(
                        0,
                        Math.min(100, Number(e.currentTarget.value) || 0)
                      );
                      aplicarVolume(v);
                    }}
                    onMouseUp={(e) => {
                      const v = Math.max(
                        0,
                        Math.min(100, Number(e.currentTarget.value) || 0)
                      );
                      aplicarVolume(v);
                    }}
                    onKeyUp={(e) => {
                      if (
                        e.key === "ArrowUp" ||
                        e.key === "ArrowDown" ||
                        e.key === "ArrowLeft" ||
                        e.key === "ArrowRight" ||
                        e.key === "Home" ||
                        e.key === "End"
                      ) {
                        const v = Math.max(
                          0,
                          Math.min(100, Number(e.currentTarget.value) || 0)
                        );
                        aplicarVolume(v);
                      }
                    }}
                    className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary"
                  />
                  <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
                    <span>Mudo</span>
                    <span>Padrão 50%</span>
                    <span>Máximo</span>
                  </div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* pt com a safe area no celular: sem a barra de cima, o conteúdo
            passou a começar na primeira linha da tela — e aberto como
            aplicativo, ou num iPhone com entalhe, a primeira linha fica atrás
            do relógio e da bateria. Do md: para cima quem já cuida disso é o
            cabeçalho. */}
        <main className="flex min-h-0 flex-1 flex-col overflow-auto pt-[env(safe-area-inset-top)] md:pt-0">
          {children ? children : null}
        </main>

        <InstalarApp />
        <AtualizacaoDisponivel />
        <AnnouncementsAutoPopup />

        <BottomNav onAbrirMenu={() => setMobileOpen(true)} />
      </div>
    </div>
  );
};

export default LoggedInLayout;
